import React, { useState, useEffect, useRef } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { WhatsAppIcon } from './WhatsAppIcon';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { mockData } from '../data/mock';
import { toast } from 'sonner';
import { trackEvent } from '../lib/analytics';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  // reCAPTCHA "No soy un robot" (Google). La clave pública llega en el build
  // desde la variable SITE_RECAPTCHA_KEY de Netlify (ver netlify.toml).
  const recaptchaKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const recaptchaRef = useRef(null);
  const widgetId = useRef(null);

  useEffect(() => {
    if (!recaptchaKey || isSubmitted) return;
    const render = () => {
      if (!window.grecaptcha || !window.grecaptcha.render || !recaptchaRef.current) return;
      if (recaptchaRef.current.childElementCount > 0) return;
      widgetId.current = window.grecaptcha.render(recaptchaRef.current, { sitekey: recaptchaKey });
    };
    if (window.grecaptcha && window.grecaptcha.render) {
      render();
    } else {
      window.onRecaptchaLoad = render;
      if (!document.getElementById('recaptcha-script')) {
        const script = document.createElement('script');
        script.id = 'recaptcha-script';
        script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit&hl=es-419';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
      }
    }
  }, [recaptchaKey, isSubmitted]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let recaptchaToken = '';
    if (recaptchaKey) {
      recaptchaToken = window.grecaptcha ? window.grecaptcha.getResponse(widgetId.current) : '';
      if (!recaptchaToken) {
        toast.error('Tildá la casilla "No soy un robot" para enviar tu consulta.');
        return;
      }
    }

    try {
      // Envío a Netlify Forms (las consultas llegan por mail y quedan
      // guardadas en el panel de Netlify → Forms → "contacto").
      const fields = { 'form-name': 'contacto', ...formData };
      if (recaptchaToken) fields['g-recaptcha-response'] = recaptchaToken;
      const body = new URLSearchParams(fields).toString();
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });

      if (response.ok) {
        setIsSubmitted(true);
        trackEvent('generate_lead', { form: 'contacto' });
        toast.success('¡Mensaje enviado! Nos contactaremos con vos a la brevedad.');
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            company: '',
            email: '',
            phone: '',
            message: ''
          });
          setIsSubmitted(false);
        }, 3000);
      } else {
        if (recaptchaKey && window.grecaptcha) window.grecaptcha.reset(widgetId.current);
        toast.error('Error al enviar el mensaje. Por favor intentá nuevamente.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Error de conexión. Por favor intentá nuevamente.');
    }
  };

  return (
    <section id="contact" className="py-11 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Contactanos
          </h2>
          <p className="text-base sm:text-lg text-gray-600">
            Estamos para ayudarte. Dejanos tu consulta y nos comunicaremos con vos a la brevedad.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact Info */}
          <div className="space-y-6 sm:space-y-8 order-2 lg:order-1">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Información de contacto</h3>
              
              <div className="space-y-3 sm:space-y-4">
                <Card className="border-2 hover:border-blue-500 transition-all duration-300">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-11 h-11 sm:w-12 sm:h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                        <a href={`mailto:${mockData.contact.email}`} className="text-blue-600 hover:text-blue-700 break-all">
                          {mockData.contact.email}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 hover:border-blue-500 transition-all duration-300">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-11 h-11 sm:w-12 sm:h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Teléfono</h4>
                        <a href={`tel:${mockData.contact.phoneLink}`} className="text-blue-600 hover:text-blue-700">
                          {mockData.contact.phone}
                        </a>
                        <p className="text-sm text-gray-600 mt-1">Lunes a Viernes: 9:30 - 17:30 hs</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 hover:border-blue-500 transition-all duration-300">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-11 h-11 sm:w-12 sm:h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <WhatsAppIcon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">WhatsApp</h4>
                        <a 
                          href="https://wa.me/5491130295481" 
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackEvent('click_whatsapp', { location: 'contacto' })}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          +54 11-3029-5481
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 hover:border-blue-500 transition-all duration-300">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-11 h-11 sm:w-12 sm:h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Dirección</h4>
                        <p className="text-gray-700">{mockData.contact.address}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden shadow-xl h-56 sm:h-64">
              <iframe
                src="https://maps.google.com/maps?q=Alvear%20867%2C%20Quilmes%2C%20Buenos%20Aires%2C%20Argentina&z=16&hl=es&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación UIQ"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="order-1 lg:order-2">
            <Card className="border-2 border-blue-100 shadow-xl">
              <CardContent className="p-5 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Envianos tu consulta</h3>
                
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                    <h4 className="text-xl font-bold text-gray-900 mb-2">¡Mensaje enviado!</h4>
                    <p className="text-gray-600">Nos contactaremos con vos a la brevedad.</p>
                  </div>
                ) : (
                  <form name="contacto" data-netlify-recaptcha="true" onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" data-testid="contact-form">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                        Nombre completo *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Juan Pérez"
                        autoComplete="name"
                        data-testid="contact-name-input"
                        className="w-full h-11 sm:h-9"
                      />
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                        Empresa *
                      </label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        required
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Industria SA"
                        autoComplete="organization"
                        data-testid="contact-company-input"
                        className="w-full h-11 sm:h-9"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Correo electrónico *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="juan@empresa.com"
                        autoComplete="email"
                        inputMode="email"
                        data-testid="contact-email-input"
                        className="w-full h-11 sm:h-9"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                        Teléfono *
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="11 1234-5678"
                        autoComplete="tel"
                        inputMode="tel"
                        data-testid="contact-phone-input"
                        className="w-full h-11 sm:h-9"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                        Mensaje *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Contanos sobre tu empresa y qué te gustaría saber..."
                        rows={4}
                        data-testid="contact-message-input"
                        className="w-full"
                      />
                    </div>

                    {recaptchaKey && (
                      <div ref={recaptchaRef} className="min-h-[78px]" data-testid="contact-recaptcha" />
                    )}

                    <Button
                      type="submit"
                      size="lg"
                      data-testid="contact-submit-btn"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 sm:py-5 transition-all duration-300 hover:shadow-xl"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      Enviar mensaje
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
